import type { JSX } from 'react';

import {
  SITE_NAME,
  SITE_DESCRIPTION,
  SITE_OG_IMAGE,
  SITE_OG_IMAGE_WIDTH,
  SITE_OG_IMAGE_HEIGHT,
  SITE_LOGO,
  canonical,
} from './site';

/**
 * 라우트 `head()` 가 돌려줄 SEO 태그 (docs/SSR.md).
 *
 * `<HeadContent />` 가 서버 HTML 과 브라우저에 같은 태그를 그린다. 제목·설명·canonical·
 * OG·JSON-LD 가 첫 응답에 들어가므로 크롤러가 JS 없이 색인하고, SPA 이동 때도 같은
 * 함수가 머리를 바꾼다.
 *
 * IMPORTANT (result gating): meta carries only public data — title, summary
 * (from the body text), canonical URL. NEVER per-option vote aggregates.
 */

/**
 * Shared schema.org Organization node, reused as the `publisher` of every
 * Article and as a standalone entity on the homepage. Aggregate/PII-free.
 */
export const PUBLISHER_LD = {
  '@type': 'Organization',
  name: SITE_NAME,
  url: canonical('/'),
  logo: { '@type': 'ImageObject', url: SITE_LOGO },
} as const;

export interface PageMeta {
  title: string;
  description: string;
  /** Absolute canonical URL. */
  url: string;
  /** og:type — "website" for lists, "article" for a detail page. */
  type?: 'website' | 'article';
  /** Optional OG/Twitter preview image (absolute URL). 아티클 썸네일. */
  image?: string;
  /** Optional schema.org JSON-LD — one object or several (already PII/aggregate-free). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Optional RSS feed autodiscovery (list pages). */
  feed?: { url: string; title: string };
  /**
   * 검색 색인에서 뺀다 — `noindex, follow`. 링크는 따라가게 둔다(목록·다른 공고로 가는 길).
   * 이 값을 쓰는 페이지는 사이트맵에서도 빠져야 한다.
   */
  noindex?: boolean;
}

/**
 * 페이지 제목 조립.
 *
 * 제목이 이미 사이트명을 품고 있으면 접미사를 붙이지 않는다. 홈 제목이
 * 「좋소 과수원 - 줘도 못먹나?」라 그대로 붙이면 이름이 두 번 나온다.
 */
function composeTitle(title: string | undefined): string {
  if (!title) return SITE_NAME;
  return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
}

type Tag = Record<string, string>;
type MetaTag = JSX.IntrinsicElements['meta'];

export function pageHead(meta: PageMeta): { meta: MetaTag[]; links: Tag[] } {
  const title = composeTitle(meta.title);
  const description = meta.description || SITE_DESCRIPTION;
  const type = meta.type ?? 'website';
  // Page-specific preview (아티클 썸네일) when present, else the site default.
  const image = meta.image ?? SITE_OG_IMAGE;

  const tags: MetaTag[] = [
    { title },
    { name: 'description', content: description },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: type },
    { property: 'og:url', content: meta.url },
    { property: 'og:locale', content: 'ko_KR' },
    { property: 'og:image', content: image },
    { property: 'og:image:alt', content: title },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: image },
  ];
  if (meta.noindex) tags.push({ name: 'robots', content: 'noindex, follow' });
  // Dimensions are known only for the site default image (1734×907); a
  // page-specific thumbnail has arbitrary dimensions, so omit them there.
  if (!meta.image) {
    tags.push({ property: 'og:image:width', content: String(SITE_OG_IMAGE_WIDTH) });
    tags.push({ property: 'og:image:height', content: String(SITE_OG_IMAGE_HEIGHT) });
  }

  const links: Tag[] = [{ rel: 'canonical', href: meta.url }];
  if (meta.feed) {
    links.push({ rel: 'alternate', type: 'application/rss+xml', title: meta.feed.title, href: meta.feed.url });
  }

  // Emit each JSON-LD node as its own <script> (e.g. Article + BreadcrumbList).
  // 라우터가 `<` 등을 이스케이프해 본문 속 </script> 가 태그를 닫지 못한다.
  const nodes = meta.jsonLd ? (Array.isArray(meta.jsonLd) ? meta.jsonLd : [meta.jsonLd]) : [];
  // 런타임은 `script:ld+json` 을 지원하지만 react-router 의 head() 타입은 <meta> 속성만 안다.
  for (const node of nodes) tags.push({ 'script:ld+json': node } as MetaTag);

  return { meta: tags, links };
}

/** Build a schema.org BreadcrumbList from ordered (name, path) pairs. */
export function breadcrumbLd(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: canonical(it.path),
    })),
  };
}
